// src/pages/Apply.tsx: public manager application form (no auth required).
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { checkRegulator, RegulatoryResult } from '../lib/regulatory';
import LocationSelect from '../components/LocationSelect';
import {
  C, JURISDICTIONS, FUND_TYPES, AUM_RANGES, TEAM_ROLES, HEARD_ABOUT,
  MAX_TEAM_MEMBERS, generatePassword, sendMail, jurisdictionLabel, fundTypeLabel,
} from '../lib/applications';

const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
const inputStyle: React.CSSProperties = {
  width: '100%', background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '3px', color: C.text, fontSize: '13px', padding: '9px 11px',
  outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px',
  textTransform: 'uppercase', display: 'block', marginBottom: '5px',
};
const btnPrimary: React.CSSProperties = {
  background: C.navy, color: C.white, border: 'none', borderRadius: '3px',
  padding: '11px 24px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
};
const btnGhost: React.CSSProperties = {
  background: C.white, color: C.textSec, border: `1px solid ${C.border}`,
  borderRadius: '3px', padding: '11px 20px', fontSize: '13px', fontWeight: 600,
  cursor: 'pointer', fontFamily: 'system-ui',
};

const STEPS = ['Contact Details', 'Firm & Regulatory', 'Fund Details', 'Team Members', 'Review & Submit'];

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: '14px' }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

const Row = ({ k, v }: { k: string; v: React.ReactNode }) => (
  <div style={{ display: 'flex', gap: '12px', padding: '7px 0', borderBottom: `1px solid ${C.borderLt}`, fontSize: '12.5px' }}>
    <div style={{ width: '170px', flexShrink: 0, color: C.muted }}>{k}</div>
    <div style={{ color: C.text, fontWeight: 500 }}>{v || '-'}</div>
  </div>
);

interface TeamRow { name: string; email: string; role: string; }

export default function Apply() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Step 1
  const [contactName, setContactName]   = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [heardAbout, setHeardAbout]     = useState('');

  // Step 2
  const [firmName, setFirmName]           = useState('');
  const [jurisdiction, setJurisdiction]   = useState('');
  const [regNumber, setRegNumber]         = useState('');
  const [checking, setChecking]           = useState(false);
  const [regResult, setRegResult]         = useState<RegulatoryResult | null>(null);
  const [isEmerging, setIsEmerging]       = useState(false);
  const [prevFirmName, setPrevFirmName]   = useState('');
  const [prevRegNumber, setPrevRegNumber] = useState('');
  const [firmWebsite, setFirmWebsite]     = useState('');
  const [firmDescription, setFirmDesc]    = useState('');
  const [firmCountry, setFirmCountry]     = useState('');
  const [firmCity, setFirmCity]           = useState('');

  // Step 3
  const [fundType, setFundType]         = useState('');
  const [strategy, setStrategy]         = useState('');
  const [aumRange, setAumRange]         = useState('');
  const [fundCount, setFundCount]       = useState('1');
  const [fundDescription, setFundDesc]  = useState('');

  // Step 4
  const [team, setTeam] = useState<TeamRow[]>([]);

  // Step 5
  const [confirmAccurate, setConfirmAccurate] = useState(false);
  const [confirmTerms, setConfirmTerms]       = useState(false);

  const jurisdictionMeta = JURISDICTIONS.find(j => j.value === jurisdiction);

  const runCheck = async () => {
    if (!jurisdiction || !regNumber.trim()) return;
    setChecking(true); setRegResult(null);
    const result = await checkRegulator(jurisdiction, regNumber.trim());
    setRegResult(result);
    setChecking(false);
  };

  const addTeamMember = () => {
    if (team.length >= MAX_TEAM_MEMBERS - 1) return;
    setTeam(t => [...t, { name: '', email: '', role: TEAM_ROLES[0] }]);
  };
  const updateTeam = (i: number, patch: Partial<TeamRow>) =>
    setTeam(t => t.map((row, idx) => idx === i ? { ...row, ...patch } : row));
  const removeTeam = (i: number) => setTeam(t => t.filter((_, idx) => idx !== i));

  const step1Valid = contactName.trim() && contactEmail.trim();
  const step2Valid = firmName.trim() && jurisdiction && regNumber.trim();
  const step3Valid = fundType && strategy.trim();

  const next = () => { setError(''); setStep(s => Math.min(5, s + 1)); };
  const back = () => { setError(''); setStep(s => Math.max(1, s - 1)); };

  const submit = async () => {
    setSubmitting(true); setError('');
    const email = contactEmail.trim().toLowerCase();
    try {
      // 1. Create the auth user. The generated password is never surfaced.
      //    The applicant signs in through the magic link they are emailed.
      const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({
        email,
        password: generatePassword(),
        options: {
          data: { full_name: contactName.trim(), role: 'manager' },
          emailRedirectTo: `${window.location.origin}/application-status`,
        },
      });
      if (signUpErr && !/already registered/i.test(signUpErr.message)) throw signUpErr;

      const profileId = signUpData?.user?.id
        || (await supabase.auth.getUser()).data.user?.id
        || null;

      if (!profileId) {
        throw new Error('This email already has an account. Please sign in first, then reapply.');
      }

      // 2. Run the regulatory check if the applicant did not already.
      const reg = regResult || await checkRegulator(jurisdiction, regNumber.trim());

      // 3. Insert the application.
      const { data: app, error: appErr } = await supabase.from('manager_applications').insert({
        contact_name: contactName.trim(),
        contact_email: email,
        contact_phone: contactPhone.trim() || null,
        heard_about: heardAbout || null,
        firm_name: firmName.trim(),
        firm_description: firmDescription.trim() || null,
        firm_website: firmWebsite.trim() || null,
        firm_country: firmCountry.trim() || null,
        firm_city: firmCity.trim() || null,
        jurisdiction,
        regulatory_number: regNumber.trim(),
        regulatory_status: reg.matchConfidence,
        regulatory_data: reg.rawData,
        regulator_firm_name: reg.firmName,
        regulator_url: reg.regulatorUrl,
        is_emerging_manager: isEmerging,
        previous_firm_name: isEmerging ? (prevFirmName.trim() || null) : null,
        previous_regulatory_number: isEmerging ? (prevRegNumber.trim() || null) : null,
        fund_type: fundType,
        strategy: strategy.trim(),
        aum_range: aumRange || null,
        fund_count: parseInt(fundCount) || 1,
        fund_description: fundDescription.trim() || null,
        status: 'submitted',
        profile_id: profileId,
      }).select().single();
      if (appErr) throw appErr;

      // 4. Team members, the primary contact is stored alongside the others.
      const rows = [
        { application_id: app.id, name: contactName.trim(), email, role: 'Primary Contact', is_primary_contact: true },
        ...team
          .filter(t => t.name.trim() && t.email.trim())
          .map(t => ({
            application_id: app.id, name: t.name.trim(),
            email: t.email.trim().toLowerCase(), role: t.role, is_primary_contact: false,
          })),
      ];
      const { error: teamErr } = await supabase.from('application_team_members').insert(rows);
      if (teamErr) console.error('Team member insert failed:', teamErr);

      // 5. Confirmation email (non-blocking).
      await sendMail('application_received', {
        contact_email: email, contact_name: contactName.trim(), firm_name: firmName.trim(),
        jurisdiction: jurisdictionLabel(jurisdiction), regulatory_number: regNumber.trim(),
      });

      setSubmitted(true);
    } catch (e: any) {
      setError(e?.message || 'Could not submit your application. Please try again.');
    }
    setSubmitting(false);
  };

  // ── Success screen ─────────────────────────────────────────────────────────
  if (submitted) return (
    <Shell>
      <div style={{ ...card, padding: '44px 40px', maxWidth: '560px', margin: '48px auto', textAlign: 'center' }}>
        <div style={{
          width: '46px', height: '46px', borderRadius: '50%', background: '#F0FDF4',
          border: '1px solid #BBF7D0', display: 'flex', alignItems: 'center',
          justifyContent: 'center', margin: '0 auto 18px', color: C.success, fontSize: '20px',
        }}>✓</div>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.text, fontWeight: 400, margin: '0 0 12px' }}>
          Application submitted successfully
        </h1>
        <p style={{ fontSize: '13.5px', color: C.textSec, lineHeight: 1.75, margin: '0 0 10px' }}>
          We have sent a confirmation email to <strong>{contactEmail}</strong>.
        </p>
        <p style={{ fontSize: '13.5px', color: C.textSec, lineHeight: 1.75, margin: '0 0 10px' }}>
          Use the link in your email to access your application portal and track your approval status.
        </p>
        <p style={{ fontSize: '13.5px', color: C.muted, lineHeight: 1.75, margin: '0 0 26px' }}>
          Our team will review your application within 2 business days.
        </p>
        <button style={btnPrimary} onClick={() => navigate('/application-status')}>Go to application portal</button>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '28px 24px 64px' }}>

        {/* ── Progress ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '26px' }}>
          {STEPS.map((label, i) => {
            const n = i + 1;
            const done = n < step;
            const current = n === step;
            return (
              <React.Fragment key={label}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: '92px' }}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: 700, marginBottom: '6px',
                    background: done ? C.success : current ? C.gold : C.white,
                    color: done || current ? C.white : C.mutedLt,
                    border: `1px solid ${done ? C.success : current ? C.gold : C.border}`,
                  }}>{done ? '✓' : n}</div>
                  <div style={{
                    fontSize: '9.5px', textAlign: 'center', lineHeight: 1.35,
                    fontWeight: current ? 700 : 500,
                    color: current ? C.navy : done ? C.textSec : C.mutedLt,
                  }}>{label}</div>
                </div>
                {i < STEPS.length - 1 && (
                  <div style={{ flex: 1, height: '1px', background: n < step ? C.success : C.border, marginTop: '14px' }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '10px 13px', marginBottom: '16px', fontSize: '12.5px', color: C.danger }}>{error}</div>
        )}

        {/* ── Step 1 ── */}
        {step === 1 && (
          <div style={{ ...card, padding: '28px 30px' }}>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: C.text, fontWeight: 400, margin: '0 0 18px' }}>Contact details</h2>
            <Field label="Full name *">
              <input style={inputStyle} value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Jane Smith" />
            </Field>
            <Field label="Email address *">
              <input style={inputStyle} type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="jane@yourfirm.com" />
            </Field>
            <Field label="Phone number (optional)">
              <input style={inputStyle} value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="+44 20 1234 5678" />
            </Field>
            <Field label="How did you hear about us?">
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={heardAbout} onChange={e => setHeardAbout(e.target.value)}>
                <option value="">Select…</option>
                {HEARD_ABOUT.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '18px' }}>
              <button style={{ ...btnPrimary, opacity: step1Valid ? 1 : 0.5 }} disabled={!step1Valid} onClick={next}>Continue</button>
            </div>
          </div>
        )}

        {/* ── Step 2 ── */}
        {step === 2 && (
          <div style={{ ...card, padding: '28px 30px' }}>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: C.text, fontWeight: 400, margin: '0 0 18px' }}>Firm and regulatory information</h2>

            <Field label="Firm name *">
              <input style={inputStyle} value={firmName} onChange={e => setFirmName(e.target.value)} placeholder="Meridian Capital LLP" />
            </Field>

            <Field label="Jurisdiction *">
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={jurisdiction}
                onChange={e => { setJurisdiction(e.target.value); setRegResult(null); }}>
                <option value="">Select jurisdiction…</option>
                {JURISDICTIONS.map(j => <option key={j.value} value={j.value}>{j.label}</option>)}
              </select>
            </Field>

            <Field label="Regulatory / authorisation number *">
              <div style={{ display: 'flex', gap: '8px' }}>
                <input style={inputStyle} value={regNumber}
                  onChange={e => { setRegNumber(e.target.value); setRegResult(null); }}
                  placeholder={jurisdictionMeta?.placeholder || 'Enter your authorisation number'} />
                <button style={{ ...btnGhost, whiteSpace: 'nowrap', opacity: jurisdiction && regNumber.trim() ? 1 : 0.5 }}
                  disabled={!jurisdiction || !regNumber.trim() || checking} onClick={runCheck}>
                  {checking ? 'Checking…' : 'Check Registration'}
                </button>
              </div>
            </Field>

            {regResult && <RegBanner result={regResult} firmName={firmName} />}

            <div style={{ borderTop: `1px solid ${C.borderLt}`, marginTop: '18px', paddingTop: '18px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', color: C.text }}>
                <input type="checkbox" checked={isEmerging} onChange={e => setIsEmerging(e.target.checked)} />
                I am launching a new fund (Emerging Manager)
              </label>
              {isEmerging && (
                <div style={{ marginTop: '14px', paddingLeft: '24px', borderLeft: `2px solid ${C.borderLt}` }}>
                  <div style={{ fontSize: '12px', color: C.muted, marginBottom: '12px', lineHeight: 1.6 }}>
                    I was previously registered at the firm below.
                  </div>
                  <Field label="Previous firm name">
                    <input style={inputStyle} value={prevFirmName} onChange={e => setPrevFirmName(e.target.value)} />
                  </Field>
                  <Field label="Previous regulatory number">
                    <input style={inputStyle} value={prevRegNumber} onChange={e => setPrevRegNumber(e.target.value)} />
                  </Field>
                </div>
              )}
            </div>

            <div style={{ borderTop: `1px solid ${C.borderLt}`, marginTop: '18px', paddingTop: '18px' }}>
              <Field label="Firm website (optional)">
                <input style={inputStyle} value={firmWebsite} onChange={e => setFirmWebsite(e.target.value)} placeholder="https://yourfirm.com" />
              </Field>
              <div style={{ marginBottom: '14px' }}>
                <LocationSelect
                  country={firmCountry} city={firmCity}
                  onCountryChange={setFirmCountry} onCityChange={setFirmCity}
                  countryLabel="HQ country" cityLabel="HQ city"
                  labelStyle={labelStyle}
                />
              </div>
              <Field label="Firm description (optional)">
                <textarea style={{ ...inputStyle, height: '88px', resize: 'vertical', lineHeight: 1.6 }}
                  value={firmDescription} onChange={e => setFirmDesc(e.target.value)} />
              </Field>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '18px' }}>
              <button style={btnGhost} onClick={back}>Back</button>
              <button style={{ ...btnPrimary, opacity: step2Valid ? 1 : 0.5 }} disabled={!step2Valid} onClick={next}>Continue</button>
            </div>
          </div>
        )}

        {/* ── Step 3 ── */}
        {step === 3 && (
          <div style={{ ...card, padding: '28px 30px' }}>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: C.text, fontWeight: 400, margin: '0 0 18px' }}>Fund details</h2>
            <Field label="Fund type *">
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={fundType} onChange={e => setFundType(e.target.value)}>
                <option value="">Select fund type…</option>
                {FUND_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </Field>
            <Field label="Primary strategy *">
              <input style={inputStyle} value={strategy} onChange={e => setStrategy(e.target.value)} placeholder="e.g. Long/Short Equity" />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Field label="AUM range">
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={aumRange} onChange={e => setAumRange(e.target.value)}>
                  <option value="">Select range…</option>
                  {AUM_RANGES.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </Field>
              <Field label="Number of funds">
                <input style={inputStyle} type="number" min="1" value={fundCount} onChange={e => setFundCount(e.target.value)} />
              </Field>
            </div>
            <Field label="Brief fund description (optional)">
              <textarea style={{ ...inputStyle, height: '88px', resize: 'vertical', lineHeight: 1.6 }}
                value={fundDescription} onChange={e => setFundDesc(e.target.value)} />
            </Field>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '18px' }}>
              <button style={btnGhost} onClick={back}>Back</button>
              <button style={{ ...btnPrimary, opacity: step3Valid ? 1 : 0.5 }} disabled={!step3Valid} onClick={next}>Continue</button>
            </div>
          </div>
        )}

        {/* ── Step 4 ── */}
        {step === 4 && (
          <div style={{ ...card, padding: '28px 30px' }}>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: C.text, fontWeight: 400, margin: '0 0 8px' }}>Team members</h2>
            <p style={{ fontSize: '13px', color: C.muted, lineHeight: 1.7, margin: '0 0 20px' }}>
              Add the team members who will have access to the platform. You can add up to {MAX_TEAM_MEMBERS} users
              under your standard subscription. You are automatically included as the primary contact.
            </p>

            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '13px 16px', marginBottom: '16px' }}>
              <div style={{ fontSize: '9.5px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '5px' }}>Primary contact</div>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: C.text }}>{contactName || '-'}</div>
              <div style={{ fontSize: '12px', color: C.muted }}>{contactEmail}</div>
            </div>

            {team.map((row, i) => (
              <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: '3px', padding: '16px', marginBottom: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: C.muted, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Team member {i + 2}</div>
                  <button onClick={() => removeTeam(i)} style={{ background: 'none', border: 'none', color: C.danger, fontSize: '12px', cursor: 'pointer', fontFamily: 'system-ui', fontWeight: 600 }}>Remove</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <Field label="Full name">
                    <input style={inputStyle} value={row.name} onChange={e => updateTeam(i, { name: e.target.value })} />
                  </Field>
                  <Field label="Email address">
                    <input style={inputStyle} type="email" value={row.email} onChange={e => updateTeam(i, { email: e.target.value })} />
                  </Field>
                </div>
                <Field label="Role">
                  <select style={{ ...inputStyle, cursor: 'pointer' }} value={row.role} onChange={e => updateTeam(i, { role: e.target.value })}>
                    {TEAM_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
              </div>
            ))}

            {team.length < MAX_TEAM_MEMBERS - 1 && (
              <button style={{ ...btnGhost, marginTop: '4px' }} onClick={addTeamMember}>+ Add {team.length ? 'another' : 'a team member'}</button>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
              <button style={btnGhost} onClick={back}>Back</button>
              <button style={btnPrimary} onClick={next}>Continue</button>
            </div>
          </div>
        )}

        {/* ── Step 5 ── */}
        {step === 5 && (
          <div style={{ ...card, padding: '28px 30px' }}>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: C.text, fontWeight: 400, margin: '0 0 18px' }}>Review and submit</h2>

            <Section title="Contact">
              <Row k="Name" v={contactName} />
              <Row k="Email" v={contactEmail} />
              <Row k="Phone" v={contactPhone} />
            </Section>

            <Section title="Firm and regulatory">
              <Row k="Firm name" v={firmName} />
              <Row k="Jurisdiction" v={jurisdictionLabel(jurisdiction)} />
              <Row k="Regulatory number" v={regNumber} />
              <Row k="Verification" v={regResult ? <RegInline result={regResult} /> : 'Not checked, our team will verify'} />
              <Row k="Emerging manager" v={isEmerging ? `Yes, previously ${prevFirmName || 'n/a'}` : 'No'} />
              <Row k="Website" v={firmWebsite} />
              <Row k="Headquarters" v={[firmCity, firmCountry].filter(Boolean).join(', ')} />
            </Section>

            <Section title="Fund">
              <Row k="Fund type" v={fundTypeLabel(fundType)} />
              <Row k="Strategy" v={strategy} />
              <Row k="AUM range" v={aumRange} />
              <Row k="Number of funds" v={fundCount} />
            </Section>

            <Section title={`Team (${team.filter(t => t.name.trim()).length + 1})`}>
              <Row k={contactName || 'Primary contact'} v="Primary contact" />
              {team.filter(t => t.name.trim()).map((t, i) => (
                <Row key={i} k={t.name} v={`${t.role} · ${t.email}`} />
              ))}
            </Section>

            <div style={{ marginTop: '22px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '12.5px', color: C.textSec, lineHeight: 1.6 }}>
                <input type="checkbox" checked={confirmAccurate} onChange={e => setConfirmAccurate(e.target.checked)} style={{ marginTop: '2px' }} />
                I confirm that all information provided is accurate and I am authorised to apply on behalf of {firmName || 'my firm'}
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '12.5px', color: C.textSec, lineHeight: 1.6 }}>
                <input type="checkbox" checked={confirmTerms} onChange={e => setConfirmTerms(e.target.checked)} style={{ marginTop: '2px' }} />
                <span>
                  I agree to the Altveno{' '}
                  <a href="/terms" target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.gold }}>Terms of Service</a>{' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.gold }}>Privacy Policy</a>
                </span>
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '22px' }}>
              <button style={btnGhost} onClick={back} disabled={submitting}>Back</button>
              <button
                style={{ ...btnPrimary, opacity: confirmAccurate && confirmTerms && !submitting ? 1 : 0.5 }}
                disabled={!confirmAccurate || !confirmTerms || submitting}
                onClick={submit}
              >{submitting ? 'Submitting…' : 'Submit application'}</button>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}

// ── Chrome ───────────────────────────────────────────────────────────────────
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ background: C.navy, padding: '22px 32px 26px' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '18px' }}>
            <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
              <path d="M 4 14 A 10 10 0 0 1 24 14" stroke="#5dc9a8" strokeWidth="2.2" strokeLinecap="round" />
              <path d="M 6 14 A 8 8 0 0 0 22 14" stroke={C.gold} strokeWidth="2.2" strokeLinecap="round" />
              <circle cx="14" cy="14" r="2.2" fill={C.gold} />
            </svg>
            <span style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '18px', color: C.white }}>Altveno</span>
          </div>
          <h1 style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '26px', color: C.white, fontWeight: 400, margin: '0 0 6px', letterSpacing: '-0.3px' }}>
            Apply for Platform Access
          </h1>
          <p style={{ fontSize: '13.5px', color: 'rgba(255,255,255,0.5)', margin: 0, lineHeight: 1.6 }}>
            Join the alternative investment platform built for managers and allocators
          </p>
        </div>
      </div>
      {children}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '18px' }}>
      <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '6px' }}>{title}</div>
      {children}
    </div>
  );
}

function RegInline({ result }: { result: RegulatoryResult }) {
  const map: Record<string, { label: string; color: string }> = {
    verified:      { label: 'Verified',            color: C.success },
    partial:       { label: 'Partial match',       color: C.amber },
    not_found:     { label: 'Not found',           color: C.danger },
    manual_review: { label: 'Manual verification', color: C.blue },
    error:         { label: 'Check unavailable',   color: C.muted },
  };
  const m = map[result.matchConfidence] || map.error;
  return <span style={{ color: m.color, fontWeight: 600 }}>{m.label}{result.firmName ? `, ${result.firmName}` : ''}</span>;
}

function RegBanner({ result, firmName }: { result: RegulatoryResult; firmName: string }) {
  const conf = result.matchConfidence;
  const tone =
    conf === 'verified' ? { bg: '#F0FDF4', bd: '#BBF7D0', fg: C.success } :
    conf === 'partial' || conf === 'not_found' ? { bg: '#FFFBEB', bd: '#FDE68A', fg: C.amber } :
    conf === 'manual_review' ? { bg: '#EFF6FF', bd: '#BFDBFE', fg: C.blue } :
    { bg: C.surface, bd: C.border, fg: C.muted };

  const heading =
    conf === 'verified' ? 'Verified' :
    conf === 'partial' ? 'Partial match' :
    conf === 'not_found' ? 'Not found' :
    conf === 'manual_review' ? 'Manual verification' :
    'Verification service unavailable';

  const body =
    conf === 'verified' || conf === 'partial'
      ? null
      : conf === 'not_found'
        ? 'Our team will verify your registration manually.'
        : conf === 'manual_review'
          ? 'Manual verification will be performed by our team.'
          : 'Verification service unavailable, our team will verify manually.';

  return (
    <div style={{ background: tone.bg, border: `1px solid ${tone.bd}`, borderRadius: '3px', padding: '12px 14px', marginBottom: '14px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: tone.fg, letterSpacing: '0.4px', textTransform: 'uppercase', marginBottom: body || result.firmName ? '6px' : 0 }}>{heading}</div>
      {result.firmName && (
        <div style={{ fontSize: '13px', color: C.text, lineHeight: 1.6 }}>
          The register shows <strong>{result.firmName}</strong>
          {result.status ? ` (${result.status})` : ''}. Is this your firm?
          {firmName && result.firmName.toLowerCase() !== firmName.toLowerCase() && (
            <div style={{ fontSize: '12px', color: C.muted, marginTop: '4px' }}>
              You entered “{firmName}”. If they differ, our team will confirm manually.
            </div>
          )}
        </div>
      )}
      {body && <div style={{ fontSize: '12.5px', color: C.textSec, lineHeight: 1.6 }}>{body}</div>}
      {result.regulatorUrl && (
        <a href={result.regulatorUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: C.teal, textDecoration: 'none', display: 'inline-block', marginTop: '6px' }}>
          View on the register ↗
        </a>
      )}
    </div>
  );
}
